import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addCustomQuestionToQuestionnaire,
  createQuestionnaireShare,
  copyQuestionsToQuestionnaire,
  deleteQuestionFromQuestionnaire,
  fetchAllQuestionnaireSections,
  getQuestionnaireById,
  getQuestionnaireResponsesById,
  publishCustomQuestionnaire,
  updateQuestionnaire,
} from '@/lib/api/questionnaires';
import { getClients } from '@/lib/api/clients';
import { getCurrentUser } from '@/lib/api/auth';
import { useDebounce } from '@/hooks/useDebounce';
import type { Client } from '@/types/client';
import {
  QuestionnaireCopyFromSourceDialog,
  type QuestionnaireCopyQuestionItem,
  type QuestionnaireCopySource,
} from '@/components/questionnaires/review/QuestionnaireCopyFromSourceDialog';
import {
  buildQuestionOptions,
  MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE,
  responseTypeRequiresOptions,
  type AdditionalQuestionResponseType,
} from '@/components/questionnaires/review/additionalQuestionConfig';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';
import {
  QuestionnaireDeleteQuestionDialog,
  QuestionnaireFinalDetailErrorSection,
  QuestionnaireFinalDetailHeaderSection,
  QuestionnaireFinalDetailLoadingSection,
  QuestionnaireManualFillDialog,
  QuestionnaireFinalDetailQuestionSections,
  QuestionnaireFinalDetailShareDialog,
  QuestionnaireFinalDetailSidebarSection,
} from './questionnaire-final-detail/sections';
import {
  ADDITIONAL_QUESTIONS_HASH,
  ADDITIONAL_QUESTIONS_SECTION_ID,
  type QuestionTypeChipData,
  type QuestionnaireSourceType,
  type ShareActionIntent,
} from './questionnaire-final-detail/types';
import { sharedAssignedClientFromClientProfile } from '@/components/questionnaires/json-driven/clientContext';
import {
  buildNewCustomQuestionPayload,
  buildSectionGroups,
  getDurationLabel,
  getQuestionnaireCodePrefix,
  getQuestionTypeChipStyle,
  getQuestionTypeLabel,
  isAdditionalQuestion,
  normalizeQuestionnaireSourceLabel,
  stripTriggerText,
  toAdditionalResponseType,
} from './questionnaire-final-detail/utils';
import { scheduleReloadForDynamicImportError } from '@/utils/dynamic-import-recovery';
import { EditQuestionnaireDialog } from '@/components/questionnaires/final-page/CustomQuestionnairesTab';

const QuestionnaireFinalDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const routeState = (location.state as { isPreviewMode?: boolean; questionnairesBackPath?: string } | null) ?? null;
  const isPreviewMode = Boolean(routeState?.isPreviewMode);
  const questionnairesBackPath =
    typeof routeState?.questionnairesBackPath === 'string' && routeState.questionnairesBackPath.trim().length > 0
      ? routeState.questionnairesBackPath
      : '/questionnaires';
  const user = getCurrentUser();
  const nutritionistId = user?.id;
  const [additionalQuestionText, setAdditionalQuestionText] = React.useState('');
  const [additionalQuestionRequired, setAdditionalQuestionRequired] = React.useState(false);
  const [additionalQuestionResponseType, setAdditionalQuestionResponseType] =
    React.useState<AdditionalQuestionResponseType>('text');
  const [additionalQuestionOptionLines, setAdditionalQuestionOptionLines] = React.useState('');
  const [isCopyDialogOpen, setIsCopyDialogOpen] = React.useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);
  const [shareActionIntent, setShareActionIntent] = React.useState<ShareActionIntent>('copy-link');
  const [selectedClientId, setSelectedClientId] = React.useState('');
  const [selectedClient, setSelectedClient] = React.useState<Client | null>(null);
  const [manualFillShareId, setManualFillShareId] = React.useState<string | null>(null);
  const [pendingDeleteQuestionId, setPendingDeleteQuestionId] = React.useState<string | null>(null);
  const [isDownloadingBlankForm, setIsDownloadingBlankForm] = React.useState(false);
  const [fillManualSearchQuery, setFillManualSearchQuery] = React.useState('');
  const [isEditNameDialogOpen, setIsEditNameDialogOpen] = React.useState(false);
  const [editQuestionnaireName, setEditQuestionnaireName] = React.useState('');
  const debouncedFillManualSearchQuery = useDebounce(fillManualSearchQuery, 300);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['questionnaire-final-detail', questionnaireId, nutritionistId],
    queryFn: async () => {
      if (!questionnaireId) throw new Error('Questionnaire ID is required');
      const section = await getQuestionnaireById(questionnaireId, nutritionistId);
      const type: QuestionnaireSourceType = section.type === 'custom' ? 'custom' : 'core';
      return { section, type };
    },
    enabled: Boolean(questionnaireId),
  });

  const { data: responseMetrics } = useQuery({
    queryKey: ['questionnaire-final-detail-response-metrics', questionnaireId],
    queryFn: () => getQuestionnaireResponsesById(questionnaireId as string),
    enabled: Boolean(questionnaireId),
    staleTime: 60_000,
  });

  const {
    data: copyCatalogData,
    refetch: refetchCopyCatalog,
  } = useQuery({
    queryKey: ['questionnaire-copy-catalog', nutritionistId],
    queryFn: () => fetchAllQuestionnaireSections(nutritionistId),
    enabled: false,
  });
  const showAdditionalQuestionsUI = !isPreviewMode || data?.type === 'custom' || data?.section?.status === 'draft';

  const { data: fillManualClients = [], isLoading: isFillManualClientsLoading } = useQuery({
    queryKey: ['questionnaire-fill-manual-clients', nutritionistId, debouncedFillManualSearchQuery],
    queryFn: async () => {
      const search = debouncedFillManualSearchQuery.trim() || undefined;
      const response = await getClients({ limit: 100, page: 1, status: 'active', search });
      return response.clients ?? [];
    },
    enabled: Boolean(nutritionistId) && isShareDialogOpen && shareActionIntent === 'fill-manually',
    staleTime: 60_000,
  });

  const additionalQuestions = React.useMemo(
    () =>
      [...(data?.section?.questions ?? [])]
        .filter((question) => isAdditionalQuestion(question))
        .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)),
    [data?.section]
  );

  const groupedSections = React.useMemo(() => {
    if (!data?.section) return [];

    const baseQuestions = (data.section.questions ?? []).filter((question) => !isAdditionalQuestion(question));
    return buildSectionGroups(baseQuestions);
  }, [data?.section]);

  const copySourceQuestionnaires = React.useMemo(() => {
    return [...(copyCatalogData?.questionnaires ?? [])]
      .filter((section) => typeof section.id === 'string' && section.id.trim().length > 0)
      .sort((a, b) =>
        normalizeQuestionnaireSourceLabel(a.title ?? a.name).localeCompare(
          normalizeQuestionnaireSourceLabel(b.title ?? b.name)
        )
      );
  }, [copyCatalogData?.questionnaires]);

  const copyDialogSources = React.useMemo<QuestionnaireCopySource[]>(
    () => [
      { id: 'all', label: 'All Questionnaires' },
      ...copySourceQuestionnaires.map((section) => ({
        id: section.id,
        label: normalizeQuestionnaireSourceLabel(section.title ?? section.name),
      })),
    ],
    [copySourceQuestionnaires]
  );

  const copyDialogQuestions = React.useMemo<QuestionnaireCopyQuestionItem[]>(() => {
    return copySourceQuestionnaires.flatMap((section) => {
      const sourceLabel = normalizeQuestionnaireSourceLabel(section.title ?? section.name);
      const codePrefix = getQuestionnaireCodePrefix(sourceLabel);
      const questions = [...(section.questions ?? [])]
        .filter(
          (question) =>
            question.isActive !== false &&
            typeof question._id === 'string' &&
            question._id.trim().length > 0 &&
            question.questionText?.trim()
        )
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      const questionIdsInSection = new Set(questions.map((q) => q._id?.trim()).filter(Boolean));

      return questions.map((question, index) => {
        const optionLabels = (question.options ?? [])
          .map((option) => option.label?.trim())
          .filter((label): label is string => Boolean(label));
        const sourceQuestionId = question._id.trim();

        const dependsOnQuestionIds: string[] = [];
        const dependsOnAnyQuestionIds: string[] = [];
        const addDep = (questionId: string, toAny: boolean) => {
          if (questionId && questionIdsInSection.has(questionId)) {
            const composite = `${section.id}:${questionId}`;
            if (toAny) {
              if (!dependsOnAnyQuestionIds.includes(composite)) dependsOnAnyQuestionIds.push(composite);
            } else {
              if (!dependsOnQuestionIds.includes(composite)) dependsOnQuestionIds.push(composite);
            }
          }
        };
        if (question.showWhen?.questionId) addDep(question.showWhen.questionId, false);
        (question.showWhenAny ?? []).forEach((cond) => cond?.questionId && addDep(cond.questionId, true));

        return {
          id: `${section.id}:${sourceQuestionId}`,
          sourceQuestionId,
          code: `${codePrefix}-${String(index + 1).padStart(2, '0')}`,
          questionText: question.questionText,
          sourceQuestionnaireId: section.id,
          typeLabel: getQuestionTypeLabel(question.type),
          chips: optionLabels.slice(0, 6),
          responseType: toAdditionalResponseType(question.type),
          required: Boolean(question.validation?.required),
          optionLines: optionLabels.join('\n'),
          dependsOnQuestionIds: dependsOnQuestionIds.length > 0 ? dependsOnQuestionIds : undefined,
          dependsOnAnyQuestionIds: dependsOnAnyQuestionIds.length > 0 ? dependsOnAnyQuestionIds : undefined,
        };
      });
    });
  }, [copySourceQuestionnaires]);

  const additionalQuestionsCount = additionalQuestions.length;
  const maxAdditionalQuestionsReached =
    additionalQuestionsCount >= MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE;

  const parsedAdditionalQuestionOptions = React.useMemo(
    () => buildQuestionOptions(additionalQuestionOptionLines),
    [additionalQuestionOptionLines]
  );

  const [expandedSectionIds, setExpandedSectionIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (expandedSectionIds.length === 0 && groupedSections.length > 0) {
      setExpandedSectionIds([groupedSections[0].id]);
    }
  }, [groupedSections]);

  React.useEffect(() => {
    if (!showAdditionalQuestionsUI || location.hash !== ADDITIONAL_QUESTIONS_HASH || !data?.section) return;

    const frameId = window.requestAnimationFrame(() => {
      document.getElementById(ADDITIONAL_QUESTIONS_SECTION_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [data?.section, location.hash, showAdditionalQuestionsUI]);

  const toggleSectionExpanded = React.useCallback((id: string) => {
    setExpandedSectionIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }, []);

  const handleSelectSectionFromSidebar = React.useCallback(
    (id: string) => {
      toggleSectionExpanded(id);
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [toggleSectionExpanded]
  );

  const handleSelectAdditionalQuestions = React.useCallback(() => {
    document.getElementById(ADDITIONAL_QUESTIONS_SECTION_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const createShareLinkMutation = useMutation({
    mutationFn: async ({ questionnaireId: targetQuestionnaireId, clientId }: { questionnaireId: string; clientId: string }) =>
      createQuestionnaireShare(targetQuestionnaireId, clientId),
  });

  const handleOpenShareDialog = React.useCallback((intent: ShareActionIntent) => {
    setShareActionIntent(intent);
    setSelectedClientId('');
    setSelectedClient(null);
    setFillManualSearchQuery('');
    setIsShareDialogOpen(true);
  }, []);

  const handleCloseShareDialog = React.useCallback(() => {
    if (createShareLinkMutation.isPending) return;
    setIsShareDialogOpen(false);
  }, [createShareLinkMutation.isPending]);

  const handleConfirmShareAction = React.useCallback(async () => {
    if (!questionnaireId) {
      showErrorToast(new Error('Questionnaire ID is required'), 'Unable to create questionnaire link');
      return;
    }

    if (!selectedClientId) {
      showWarningToast('Select a client', 'Choose a client before generating a questionnaire link.');
      return;
    }

    try {
      const shareId = await createShareLinkMutation.mutateAsync({
        questionnaireId,
        clientId: selectedClientId,
      });

      const shareableLink = `${window.location.origin}/questionnaire/share/${encodeURIComponent(shareId)}`;
      const clientName = selectedClient?.firstName?.trim() || selectedClient?.fullName?.trim() || 'selected client';

      if (shareActionIntent === 'fill-manually') {
        setManualFillShareId(shareId);
        showSuccessToast('Questionnaire opened', `Opened questionnaire form for ${clientName}.`);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareableLink);
        showSuccessToast('Link copied', `Questionnaire link copied for ${clientName}.`);
      } else {
        window.prompt('Copy questionnaire link', shareableLink);
        showSuccessToast('Link generated', 'Clipboard is unavailable, so the link is ready to copy.');
      }

      setIsShareDialogOpen(false);
    } catch (mutationError) {
      showErrorToast(
        mutationError,
        shareActionIntent === 'fill-manually'
          ? 'Unable to open questionnaire form'
          : 'Unable to copy questionnaire link'
      );
    }
  }, [
    createShareLinkMutation,
    questionnaireId,
    selectedClient,
    selectedClientId,
    shareActionIntent,
  ]);

  const handleOpenCopyDialog = React.useCallback(() => {
    setIsCopyDialogOpen(true);
    void refetchCopyCatalog();
  }, [refetchCopyCatalog]);

  const handleCloseCopyDialog = React.useCallback(() => {
    setIsCopyDialogOpen(false);
  }, []);

  const copyQuestionMutation = useMutation({
    mutationFn: async (questions: QuestionnaireCopyQuestionItem[]) => {
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is required');
      }

      for (const question of questions) {
        await copyQuestionsToQuestionnaire(questionnaireId, {
          sourceQuestionnaireId: question.sourceQuestionnaireId,
          questionIds: [question.sourceQuestionId],
        });
      }

      return questions.length;
    },
    onSuccess: async (copiedCount) => {
      await queryClient.invalidateQueries({
        queryKey: ['questionnaire-final-detail', questionnaireId, nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaire-final-detail-response-metrics', questionnaireId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'core', nutritionistId],
      });

      setIsCopyDialogOpen(false);
      showSuccessToast(
        'Questions copied',
        `${copiedCount} question${copiedCount === 1 ? '' : 's'} copied successfully.`
      );
    },
    onError: (mutationError) => {
      showErrorToast(mutationError, 'Unable to copy question');
    },
  });

  const handleDoneCopyQuestions = React.useCallback(
    (questions: QuestionnaireCopyQuestionItem[]) => {
      copyQuestionMutation.mutate(questions);
    },
    [copyQuestionMutation]
  );

  const deleteQuestionMutation = useMutation({
    mutationFn: async (questionId: string) => {
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is required');
      }

      return deleteQuestionFromQuestionnaire(questionnaireId, questionId);
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['questionnaire-final-detail', questionnaireId, nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaire-final-detail-response-metrics', questionnaireId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'core', nutritionistId],
      });

      setPendingDeleteQuestionId(null);
      showSuccessToast('Question deleted', response.message);
    },
    onError: (mutationError) => {
      showErrorToast(mutationError, 'Unable to delete question');
    },
  });

  const handleRequestDeleteQuestion = React.useCallback(
    (questionId: string) => {
      setPendingDeleteQuestionId(questionId);
    },
    []
  );

  const handleCloseDeleteQuestionDialog = React.useCallback(() => {
    setPendingDeleteQuestionId(null);
  }, []);

  const handleConfirmDeleteQuestion = React.useCallback(() => {
    if (!pendingDeleteQuestionId) return;
    deleteQuestionMutation.mutate(pendingDeleteQuestionId);
  }, [deleteQuestionMutation, pendingDeleteQuestionId]);

  const saveAdditionalQuestionMutation = useMutation({
    mutationFn: async () => {
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is required');
      }

      const trimmedQuestionText = additionalQuestionText.trim();
      if (trimmedQuestionText.length === 0) {
        throw new Error('Question text is required');
      }

      if (
        responseTypeRequiresOptions(additionalQuestionResponseType) &&
        parsedAdditionalQuestionOptions.length === 0
      ) {
        throw new Error('Add at least one option for the selected response type');
      }

      if (additionalQuestionsCount >= MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE) {
        throw new Error(
          `A questionnaire can have up to ${MAX_CUSTOM_QUESTIONS_PER_QUESTIONNAIRE} custom questions`
        );
      }

      return addCustomQuestionToQuestionnaire(
        questionnaireId,
        buildNewCustomQuestionPayload(
          trimmedQuestionText,
          additionalQuestionRequired,
          additionalQuestionResponseType,
          additionalQuestionOptionLines
        )
      );
    },
    onSuccess: async (response) => {
      setAdditionalQuestionText('');
      setAdditionalQuestionRequired(false);
      setAdditionalQuestionResponseType('text');
      setAdditionalQuestionOptionLines('');

      await queryClient.invalidateQueries({
        queryKey: ['questionnaire-final-detail', questionnaireId, nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'core', nutritionistId],
      });

      showSuccessToast('Custom question saved', response.message);
    },
    onError: (mutationError) => {
      showErrorToast(mutationError, 'Unable to save custom question');
    },
  });

  const publishQuestionnaireMutation = useMutation({
    mutationFn: async () => {
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is required');
      }
      return publishCustomQuestionnaire(questionnaireId);
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: ['questionnaire-final-detail', questionnaireId, nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'draft', 'custom', nutritionistId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['questionnaireSections', 'final-page', 'core', nutritionistId],
      });
      showSuccessToast('Questionnaire published', response.message);
    },
    onError: (mutationError) => {
      showErrorToast(mutationError, 'Unable to publish questionnaire');
    },
  });

  const updateQuestionnaireNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!questionnaireId) {
        throw new Error('Questionnaire ID is required');
      }
      return updateQuestionnaire(questionnaireId, { name });
    },
    onSuccess: async (response) => {
      setIsEditNameDialogOpen(false);
      setEditQuestionnaireName('');
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['questionnaire-final-detail', questionnaireId, nutritionistId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['questionnaireSections', 'final-page', 'draft', 'custom', nutritionistId],
        }),
      ]);
      showSuccessToast('Questionnaire updated', response.message);
    },
    onError: (mutationError) => {
      showErrorToast(mutationError, 'Unable to update questionnaire');
    },
  });

  const handleSaveAdditionalQuestion = React.useCallback(() => {
    saveAdditionalQuestionMutation.mutate();
  }, [saveAdditionalQuestionMutation]);

  const handleOpenSubmittedResponsePreview = React.useCallback(
    (instanceId: string) => {
      if (!questionnaireId || !selectedClientId) return;

      const clientName =
        selectedClient?.fullName?.trim()
        || [selectedClient?.firstName, selectedClient?.lastName].filter(Boolean).join(' ').trim();
      const query = new URLSearchParams({ clientId: selectedClientId });
      if (clientName) {
        query.set('clientName', clientName);
      }
      navigate(
        `/questionnaires/${encodeURIComponent(questionnaireId)}/responses/${encodeURIComponent(instanceId)}?${query.toString()}`
      );
    },
    [navigate, questionnaireId, selectedClient, selectedClientId]
  );

  const handleDownloadBlankForm = React.useCallback(() => {
    if (isDownloadingBlankForm) return;

    if (!data?.section) {
      showErrorToast(new Error('Questionnaire data is unavailable'), 'Unable to download questionnaire form');
      return;
    }

    const printableSections = [
      ...groupedSections.map((group) => ({
        label: group.label,
        questions: group.questions.filter((question) => question.isActive !== false),
      })),
      ...(additionalQuestions.length > 0
        ? [
            {
              label: 'Additional Questions',
              questions: additionalQuestions.filter((question) => question.isActive !== false),
            },
          ]
        : []),
    ].filter((group) => group.questions.length > 0);

    if (printableSections.length === 0) {
      showWarningToast('No questions available', 'This questionnaire has no active questions to export.');
      return;
    }

    setIsDownloadingBlankForm(true);
    void (async () => {
      try {
        const { downloadPrintableQuestionnaireForm } = await import(
          './questionnaire-final-detail/printableQuestionnaireForm'
        );
        await downloadPrintableQuestionnaireForm({
          title: data.section.title || 'Questionnaire',
          questionnaireTypeLabel: data.type === 'core' ? 'Core Questionnaire' : 'Custom Questionnaire',
          description: stripTriggerText(data.section.description),
          clinicLogoUrl: user?.clinicLogoUrl ?? undefined,
          sections: printableSections,
        });

        showSuccessToast('PDF downloaded', 'Printable blank questionnaire PDF downloaded successfully.');
      } catch (downloadError) {
        if (scheduleReloadForDynamicImportError(downloadError, {
          storageKey: 'caloriescience:questionnaire-pdf-export-reload-at',
        })) {
          showWarningToast(
            'App update detected',
            'Reloading the latest app files. Please try exporting again after the page reloads.',
          );
          return;
        }
        showErrorToast(downloadError, 'Unable to download questionnaire PDF');
      } finally {
        setIsDownloadingBlankForm(false);
      }
    })();
  }, [additionalQuestions, data, groupedSections, isDownloadingBlankForm, user?.clinicLogoUrl]);

  if (isLoading) {
    return <QuestionnaireFinalDetailLoadingSection />;
  }

  if (isError || !data) {
    return (
      <QuestionnaireFinalDetailErrorSection
        message={(error as Error | undefined)?.message ?? 'The questionnaire detail screen could not be loaded.'}
        onBack={() => navigate('/questionnaires')}
      />
    );
  }

  const { section, type } = data;
  const description = stripTriggerText(section.description);
  const questionCount = responseMetrics?.questionsCount ?? 0;
  const assignedClientsCount = responseMetrics?.assignedClientsCount ?? 0;
  const responsesCount = responseMetrics?.responsesCount ?? 0;
  const durationLabel =
    responseMetrics?.estimatedMinutes != null && responseMetrics.estimatedMinutes > 0
      ? `${responseMetrics.estimatedMinutes} minutes`
      : getDurationLabel(section);
  const questionnaireTypeLabel = type === 'core' ? 'Core Questionnaire' : 'Custom Questionnaire';
  const isDraftQuestionnaire = section.status === 'draft';
  const uniqueTypes = Array.from(new Set((section.questions ?? []).map((question) => question.type)));
  const questionTypeChips: QuestionTypeChipData[] = uniqueTypes.map((questionType) => ({
    type: questionType,
    label: getQuestionTypeLabel(questionType).toLowerCase(),
    style: getQuestionTypeChipStyle(questionType),
  }));

  return (
    <Box data-demo-id="questionnaire-detail-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f8fafc',
          p: { xs: 2.5, md: 3.5 },
        }}
      >
        <QuestionnaireFinalDetailHeaderSection
          section={section}
          questionnairesBackPath={questionnairesBackPath}
          questionnaireTypeLabel={questionnaireTypeLabel}
          description={description}
          questionCount={questionCount}
          durationLabel={durationLabel}
          assignedClientsCount={assignedClientsCount}
          responsesCount={responsesCount}
          type={type}
          isDraftQuestionnaire={isDraftQuestionnaire}
          publishPending={publishQuestionnaireMutation.isPending}
          downloadPending={isDownloadingBlankForm}
          onEditName={() => {
            setEditQuestionnaireName(section.title);
            setIsEditNameDialogOpen(true);
          }}
          onAssignClients={() => navigate(`/questionnaires/${section.id}/assign`)}
          onPublish={() => publishQuestionnaireMutation.mutate()}
          onOpenShareDialog={handleOpenShareDialog}
          onDownloadBlankForm={handleDownloadBlankForm}
        />

        <Box
          sx={{
            mt: 2.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '260px minmax(0, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <QuestionnaireFinalDetailSidebarSection
            groupedSections={groupedSections}
            expandedSectionIds={expandedSectionIds}
            onSelectSection={handleSelectSectionFromSidebar}
            additionalQuestionsCount={additionalQuestionsCount}
            isAdditionalQuestionsActive={location.hash === ADDITIONAL_QUESTIONS_HASH}
            onSelectAdditionalQuestions={handleSelectAdditionalQuestions}
            questionTypeChips={questionTypeChips}
            showAdditionalQuestionsSection={showAdditionalQuestionsUI}
          />

          <QuestionnaireFinalDetailQuestionSections
            groupedSections={groupedSections}
            expandedSectionIds={expandedSectionIds}
            onToggleSectionExpanded={toggleSectionExpanded}
            canDeleteQuestions={type === 'custom'}
            deletePendingQuestionId={deleteQuestionMutation.isPending ? deleteQuestionMutation.variables : undefined}
            onRequestDeleteQuestion={handleRequestDeleteQuestion}
            additionalQuestions={additionalQuestions}
            additionalQuestionsCount={additionalQuestionsCount}
            additionalQuestionText={additionalQuestionText}
            additionalQuestionRequired={additionalQuestionRequired}
            additionalQuestionResponseType={additionalQuestionResponseType}
            additionalQuestionOptionLines={additionalQuestionOptionLines}
            onAdditionalQuestionTextChange={setAdditionalQuestionText}
            onAdditionalQuestionRequiredChange={setAdditionalQuestionRequired}
            onAdditionalQuestionResponseTypeChange={setAdditionalQuestionResponseType}
            onAdditionalQuestionOptionLinesChange={setAdditionalQuestionOptionLines}
            onSaveAdditionalQuestion={handleSaveAdditionalQuestion}
            onOpenCopyDialog={handleOpenCopyDialog}
            isSavingAdditionalQuestion={saveAdditionalQuestionMutation.isPending}
            maxAdditionalQuestionsReached={maxAdditionalQuestionsReached}
            isSaveAdditionalQuestionDisabled={
              saveAdditionalQuestionMutation.isPending ||
              additionalQuestionText.trim().length === 0 ||
              (responseTypeRequiresOptions(additionalQuestionResponseType) &&
                parsedAdditionalQuestionOptions.length === 0)
            }
            showAdditionalQuestionsPanel={showAdditionalQuestionsUI}
          />
        </Box>
      </Paper>

      <QuestionnaireDeleteQuestionDialog
        open={Boolean(pendingDeleteQuestionId)}
        isSubmitting={deleteQuestionMutation.isPending}
        onClose={handleCloseDeleteQuestionDialog}
        onConfirm={handleConfirmDeleteQuestion}
      />

      <EditQuestionnaireDialog
        open={isEditNameDialogOpen}
        value={editQuestionnaireName}
        isSubmitting={updateQuestionnaireNameMutation.isPending}
        onChange={setEditQuestionnaireName}
        onClose={() => {
          setIsEditNameDialogOpen(false);
          setEditQuestionnaireName('');
        }}
        onConfirm={() => updateQuestionnaireNameMutation.mutate(editQuestionnaireName.trim())}
      />

      <QuestionnaireCopyFromSourceDialog
        open={isCopyDialogOpen}
        onClose={handleCloseCopyDialog}
        sources={copyDialogSources}
        questions={copyDialogQuestions}
        isSubmitting={copyQuestionMutation.isPending}
        onDone={handleDoneCopyQuestions}
      />

      <QuestionnaireFinalDetailShareDialog
        open={isShareDialogOpen}
        shareActionIntent={shareActionIntent}
        isSubmitting={createShareLinkMutation.isPending}
        selectedClientId={selectedClientId}
        searchText={fillManualSearchQuery}
        clients={fillManualClients}
        isClientsLoading={isFillManualClientsLoading}
        onClose={handleCloseShareDialog}
        onConfirm={handleConfirmShareAction}
        onSearchTextChange={setFillManualSearchQuery}
        onClientChange={(clientId, client) => {
          setSelectedClientId(clientId);
          setSelectedClient(client);
        }}
      />

      <QuestionnaireManualFillDialog
        open={Boolean(manualFillShareId)}
        shareId={manualFillShareId}
        questionnaireTitle={section.title}
        clientName={selectedClient?.fullName?.trim() || selectedClient?.firstName?.trim() || 'Selected client'}
        assignedClientProfile={
          selectedClient ? sharedAssignedClientFromClientProfile(selectedClient) : undefined
        }
        onClose={() => setManualFillShareId(null)}
        onSubmitted={handleOpenSubmittedResponsePreview}
      />
    </Box>
  );
};

export default QuestionnaireFinalDetailPage;
